import { useCallback } from 'react';
import { message } from 'antd';
import {
  UseSaveAllocationProps,
  SaveParams,
  AllocationConfigItem,
} from 'store/ducks/Global/types';
import { branchOp } from 'store/ducks/DataEntry/Branch';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import contractualArrangementsOp from 'store/ducks/DataEntry/ContractualInformation/operations';
import ApiService from 'utils/ApiCalls';

const ALLOCATION_CONFIG: Record<string, AllocationConfigItem> = {
  branch: {
    fetchFn: () => branchOp.fetchAllBranches(),
    saveFn: (payload) => branchOp.insertBranches(payload),
    fetchByEntityFn: (id) => branchOp.fetchBranchesByEntity({ entity_id: id }),
    parentKey: 'entity_id',
    dataKey: 'Branch',
    entityNameKey: 'entity_name',
  },
  function: {
    fetchFn: () => functionOp.fetchAllFunctions(),
    saveFn: (payload) => functionOp.insertFunctions(payload),
    fetchByEntityFn: (id) =>
      functionOp.fetchFunctionsByEntity({ entity_id: id }),
    parentKey: 'entity_id',
    dataKey: 'Function',
    entityNameKey: 'entity_name',
  },
  tpsp: {
    fetchFn: () => tppOp.fetchAllThirdPartyServiceProviders(),
    saveFn: (payload) => tppOp.fetchTPP(payload),
    fetchByEntityFn: (id) =>
      ApiService.getListOfThirdPartyServiceProvidersByEntity(id),
    parentKey: 'entity_id',
    dataKey: 'ThirdPartyServiceProvider',
    entityNameKey: 'entity_name',
  },
  contractualArrangements: {
    fetchFn: () =>
      contractualArrangementsOp.fetchListOfContractualArrangements(),
    saveFn: (payload) =>
      contractualArrangementsOp.insertContractualDetails(payload),
    fetchByEntityFn: (id) =>
      contractualArrangementsOp.fetchListOfContractualArrangementsByEntity(id),
    parentKey: 'third_party_service_provider_id',
    dataKey: 'contractual_arrangement',
    entityNameKey: 'tpp_name',
  },
  ictServices: {
    fetchFn: () => ApiService.getListOfICTServices(),
    saveFn: (payload) => ApiService.postICTServices(payload),
    fetchByEntityFn: (id) =>
      ApiService.getListOfICTServicesByContractualArrangement(id),
    parentKey: 'contractual_arrangement_id',
    dataKey: 'ict_service_entries',
    entityNameKey: 'contractual_arrangement_name',
  },
  ictSystems: {
    fetchFn: () => ApiService.getListOfICTSystems(),
    saveFn: (payload) => ApiService.postICTServices(payload),
    fetchByEntityFn: (id) => ApiService.getListOfICTSystemsByEntity(id),
    parentKey: 'entity_id',
    dataKey: 'ict_system_entries',
    entityNameKey: 'entity_name',
    buildPayload: (items, parent) => ({
      entity_name: parent.label,
      ict_systems_entries: items,
    }),
  },
};

export const useSaveAllocation = ({ onSetLoading }: UseSaveAllocationProps) => {
  const save = useCallback(
    async ({
      id,
      parent,
      selectedItems,
      successMessage,
      isRightToLeft,
    }: SaveParams & { isRightToLeft?: boolean }) => {
      if (!parent) {
        message.warning('Please select a parent first');
        return;
      }

      try {
        onSetLoading(id, true);

        const config = ALLOCATION_CONFIG[id as keyof typeof ALLOCATION_CONFIG];
        if (!config) {
          throw new Error(`Unknown allocation type: ${id}`);
        }

        // Fetch full details for selected items
        const response = await config.fetchByEntityFn(parent.id);
        const allItems = Array.isArray(response?.data)
          ? response.data
          : response?.data
            ? [response.data]
            : [];

        const itemsToSave = selectedItems.map((item: Record<string, any>) => {
          const fullDetails = allItems.find(
            (a: Record<string, any>) => a.id === Number(item.key),
          );
          return fullDetails || item;
        });

        const itemsWithParent = itemsToSave.map((item: Record<string, any>) => {
          const itemData = { ...item };

          itemData[config.parentKey] = isRightToLeft ? 1 : parent.id;

          delete itemData.key;
          delete itemData.title;

          return itemData;
        });

        let payload: Record<string, any>;

        if (config.buildPayload) {
          payload = config.buildPayload(itemsWithParent, parent);
        } else {
          payload = {
            [config.entityNameKey]: parent.label,
            [config.dataKey]: itemsWithParent,
          };
        }

        await config.saveFn(payload);
        message.success(successMessage);
      } catch (err) {
        console.error(err);
        message.error('Save failed');
      } finally {
        onSetLoading(id, false);
      }
    },
    [onSetLoading],
  );

  return { save };
};
